import { useCallback } from 'react'
import { useAttachmentStore, useRemainingAttachments } from '../../../../store'
import { useAddChatToast } from '../../../../store/toasts'
import { useTrackAddAttachment } from '../../../../hooks/attachments-hooks'
import { isFileAccepted } from '@shared/file-grants/uploadable-files'
import { processFile } from '../../../../utils/fileUtils'

/** The accepted files on a clipboard, read synchronously — see `attachFiles`. */
export const acceptedClipboardFiles = (clipboardData: DataTransfer | null): Array<File> =>
  Array.from(clipboardData?.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null && isFileAccepted(file))

/**
 * Attaches pasted files, capped at the remaining attachment slots. Both paste
 * paths — the composer's own handler and the document-level one that catches a
 * paste from anywhere else — go through here so the cap and the toast can't
 * drift apart. Callers must collect the files synchronously
 * (`acceptedClipboardFiles`): `clipboardData` is gone by the first await.
 */
export const useAttachFiles = () => {
  const { addFileAttachment } = useAttachmentStore()
  const remainingAttachments = useRemainingAttachments()
  const addChatToast = useAddChatToast()
  const trackAddAttachment = useTrackAddAttachment()

  return useCallback(
    async (files: Array<File>) => {
      if (!files.length) return
      if (remainingAttachments <= 0) {
        addChatToast({ message: 'Maximum attachments reached!', type: 'error' })
        return
      }

      await Promise.all(
        files.slice(0, remainingAttachments).map(async (file) => {
          const attach = await processFile(file)
          if (attach.error) {
            addChatToast({ message: attach.error, type: 'error' })
            return
          }
          addFileAttachment(attach.file, attach.value)
        }),
      )
      trackAddAttachment({ type: 'file', trigger: 'Paste File(s)' })
    },
    [addFileAttachment, addChatToast, remainingAttachments, trackAddAttachment],
  )
}
